import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { listPageDialogPaperProps } from '../theme/darkDialog';
import { apiService } from '../services/api';

export type NewEstimateStep = 'choose' | 'manual' | 'upload';

export interface NewEstimateDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  /** When set (e.g. from estimate detail), DXF lines replace prior DXF-derived lines on this estimate. */
  estimateId?: string;
  /** Initial step when dialog opens. Default 'choose'. Use 'upload' when opening from estimate detail "Upload Files". */
  initialStep?: NewEstimateStep;
  /** Called when an estimate is created (manual or upload). Parent can navigate or refresh. */
  onSuccess?: (estimateId: string) => void;
}

const NewEstimateDialog: React.FC<NewEstimateDialogProps> = ({
  open,
  onClose,
  projectId,
  estimateId,
  initialStep = 'choose',
  onSuccess,
}) => {
  const [step, setStep] = useState<NewEstimateStep>(initialStep);

  // Manual flow state
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Upload flow state
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    estimateId: string;
    extractedCount: number;
    matchedCount: number;
    unmatchedCount: number;
    estimateLineCount: number;
  } | null>(null);
  const [drawingUnitScale, setDrawingUnitScale] = useState(1);
  const [scalePreset, setScalePreset] = useState<string>('1');
  const [customScale, setCustomScale] = useState('');
  const DRAWING_PRESETS: { id: string; label: string; value: number }[] = [
    { id: '1', label: 'Meters (1 = 1 m)', value: 1 },
    { id: '0.001', label: 'Millimeters (×0.001)', value: 0.001 },
    { id: '0.3048', label: 'US survey feet (×0.3048)', value: 0.3048 },
    { id: 'custom', label: 'Custom…', value: -1 },
  ];
  const [uploadError, setUploadError] = useState<string | null>(null);

  const createStartedRef = useRef(false);

  useEffect(() => {
    if (open) {
      setStep(initialStep);
      setCreateError(null);
      setUploadError(null);
      setFile(null);
      setUploadResult(null);
      setScalePreset('1');
      setCustomScale('');
      setDrawingUnitScale(1);
      createStartedRef.current = false;
    }
  }, [open, initialStep]);

  const handleClose = () => {
    setStep(initialStep);
    setCreating(false);
    setCreateError(null);
    setUploading(false);
    setUploadError(null);
    setFile(null);
    setUploadResult(null);
    setScalePreset('1');
    setDrawingUnitScale(1);
    setCustomScale('');
    createStartedRef.current = false;
    onClose();
  };

  const handleBack = () => {
    if (step === 'choose') {
      handleClose();
    } else {
      setStep('choose');
      setCreateError(null);
      setUploadError(null);
      setFile(null);
      setUploadResult(null);
    }
  };

  // Manual: create estimate and finish (run once when entering manual step)
  useEffect(() => {
    if (!open || step !== 'manual') return;
    if (createStartedRef.current) return;
    createStartedRef.current = true;
    setCreating(true);
    setCreateError(null);
    apiService
      .createEstimate({ projectId, title: 'Manual estimate' })
      .then((estimate) => {
        onSuccess?.(estimate.estimateId);
        handleClose();
      })
      .catch((err) => {
        console.error(err);
        setCreateError('Failed to create estimate');
        setCreating(false);
        createStartedRef.current = false;
      });
  }, [open, step, projectId]);

  const handleUpload = async () => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.dxf')) {
      setUploadError('Only .dxf files are supported.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const res = await apiService.uploadAutocad(projectId, file, drawingUnitScale, estimateId);
      setUploadResult({
        estimateId: res.estimateId,
        extractedCount: res.extractedCount,
        matchedCount: res.matchedCount,
        unmatchedCount: res.unmatchedCount,
        estimateLineCount: res.estimateLineCount,
      });
    } catch (err) {
      console.error(err);
      setUploadError('Upload failed. Check file format and try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleViewEstimate = (estimateId: string) => {
    onSuccess?.(estimateId);
    handleClose();
  };

  const title =
    step === 'choose'
      ? 'New Estimate'
      : step === 'manual'
        ? 'New Estimate — Manual'
        : estimateId != null
          ? 'Upload DXF to this estimate'
          : 'Upload DXF';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={listPageDialogPaperProps}
    >
      <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ padding: '20px 24px 24px' }}>
        {step === 'choose' && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-slate-400">
              Choose how to create your estimate: manual entry or upload from a DXF file.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setStep('manual')}
                className="flex flex-col items-start text-left bg-elevation-dark rounded-xl p-4 border border-border-dark cursor-pointer transition-colors hover:border-primary/50 hover:bg-white/[0.02]"
              >
                <span className="material-symbols-outlined text-3xl text-primary mb-2">edit_note</span>
                <h3 className="text-white font-semibold mb-1">Manual Entry</h3>
                <p className="text-slate-400 text-xs">
                  Add line items from the Cost Library. Choose categories and items, enter quantities.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setStep('upload')}
                className="flex flex-col items-start text-left bg-elevation-dark rounded-xl p-4 border border-border-dark cursor-pointer transition-colors hover:border-primary/50 hover:bg-white/[0.02]"
              >
                <span className="material-symbols-outlined text-3xl text-primary mb-2">upload_file</span>
                <h3 className="text-white font-semibold mb-1">Upload AutoCAD File</h3>
                <p className="text-slate-400 text-xs">
                  Upload a DXF file. Items are matched by Item Code to the Cost Library.
                </p>
              </button>
            </div>
            <div className="pt-2">
              <button
                type="button"
                onClick={handleBack}
                className="flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-600 bg-slate-700 px-4 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-600"
              >
                <span className="material-symbols-outlined text-base">close</span>
                Cancel
              </button>
            </div>
          </div>
        )}

        {step === 'manual' && (
          <div className="flex flex-col gap-4 py-2">
            {creating && !createError && (
              <div className="flex flex-col items-center gap-3 text-slate-400 py-6">
                <span className="material-symbols-outlined animate-spin text-4xl text-primary">progress_activity</span>
                <p className="text-sm">Creating estimate...</p>
              </div>
            )}
            {createError && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-400 text-sm">
                {createError}
              </div>
            )}
          </div>
        )}

        {step === 'upload' && (
          <div className="flex flex-col gap-4 py-2">
            <p className="text-sm text-slate-400">
              Upload a DXF file. Layers and blocks map to cost lines; BOM rules can expand one layer to multiple materials. Unmatched codes produce no lines.
              {estimateId != null && (
                <span className="block mt-2 text-amber-200/90">
                  Existing lines from a previous DXF import on this estimate will be replaced; manually added lines are kept.
                </span>
              )}
            </p>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-medium text-slate-400">Drawing units → meters</label>
              <select
                className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200"
                value={scalePreset}
                onChange={(e) => {
                  const id = e.target.value;
                  setScalePreset(id);
                  if (id === 'custom') return;
                  const p = DRAWING_PRESETS.find((x) => x.id === id);
                  if (p && p.id !== 'custom') setDrawingUnitScale(p.value);
                }}
              >
                {DRAWING_PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              {scalePreset === 'custom' && (
                <input
                  type="number"
                  min={0.000000001}
                  step="any"
                  placeholder="Scale (1 DXF unit = ? meters)"
                  className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200"
                  value={customScale}
                  onChange={(e) => {
                    setCustomScale(e.target.value);
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v) && v > 0) setDrawingUnitScale(v);
                  }}
                />
              )}
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <label className="flex h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border border-slate-600 bg-slate-700 px-4 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-600">
                  <span className="material-symbols-outlined text-base">folder_open</span>
                  Choose file
                  <input
                    type="file"
                    accept=".dxf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      setFile(f ?? null);
                      setUploadResult(null);
                      setUploadError(null);
                    }}
                  />
                </label>
                {file && <span className="text-slate-300 text-sm">{file.name}</span>}
              </div>

              {uploadError && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-400 text-sm">
                  {uploadError}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="flex h-10 min-w-[84px] items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-bold text-white transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <span className="material-symbols-outlined animate-spin text-xl">progress_activity</span>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-xl">upload</span>
                      Upload
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex h-10 items-center justify-center rounded-lg border border-slate-600 bg-slate-700 px-4 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-600"
                >
                  Cancel
                </button>
              </div>

              {uploadResult && (
                <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2 text-green-400 text-sm">
                  <p className="mb-2">
                    Extracted codes: {uploadResult.extractedCount}, Matched codes: {uploadResult.matchedCount}, Unmatched:{' '}
                    {uploadResult.unmatchedCount}. Estimate lines: {uploadResult.estimateLineCount}.
                  </p>
                  <button
                    type="button"
                    onClick={() => handleViewEstimate(uploadResult.estimateId)}
                    className="rounded-lg bg-primary px-3 py-1.5 text-sm font-bold text-white hover:bg-primary/90"
                  >
                    Save &amp; View estimate
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewEstimateDialog;
